import React, { useEffect, useState } from 'react';
import MainLayout from '../components/Layout/MainLayout';
import logsService from '../services/logsService';

const ChairmanLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [hrUsers, setHrUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [appliedFilters, setAppliedFilters] = useState({});

  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    role: '',
    userId: '',
    search: ''
  });

  const fetchLogs = async (page = 1, activeFilters = appliedFilters) => {
    setLoading(true);
    setError('');
    setLogs([]);

    try {
      const response = await logsService.getHRActivityLogs({
        ...activeFilters,
        page,
        limit: pagination.limit
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to load logs');
      }

      setLogs(response.data.items || []);
      setPagination(response.data.pagination || { page: 1, limit: 25, total: 0, totalPages: 1 });
    } catch (err) {
      setLogs([]);
      setError(err.response?.data?.error || err.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialFilters = {
      fromDate: '',
      toDate: '',
      role: '',
      userId: '',
      search: ''
    };
    setAppliedFilters(initialFilters);
    fetchLogs(1, initialFilters);
    fetchHRUsers();
  }, []);

  useEffect(() => {
    fetchHRUsers();
  }, [filters.role]);

  const fetchHRUsers = async () => {
    try {
      const response = await logsService.getHRUsers({ role: filters.role });
      if (!response.success) {
        return;
      }

      setHrUsers(response.data || []);
    } catch (err) {
      console.error('Failed to load HR users:', err);
    }
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();
    const nextFilters = {
      ...filters,
      userId: filters.userId ? String(filters.userId) : ''
    };
    setAppliedFilters(nextFilters);
    fetchLogs(1, nextFilters);
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString();
  };

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">HR Activity Logs</h1>
          <p className="text-sm text-gray-600 mt-1">Track HR/HRO page visits with date, time, URL, IP, and device id.</p>
        </div>

        <form onSubmit={handleApplyFilters} className="bg-white rounded-lg border border-gray-200 p-4 grid grid-cols-1 md:grid-cols-6 gap-3">
          <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))}
            className="border rounded-md px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={filters.toDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))}
            className="border rounded-md px-3 py-2 text-sm"
          />
          <select
            value={filters.role}
            onChange={(e) => setFilters((prev) => ({ ...prev, role: e.target.value, userId: '' }))}
            className="border rounded-md px-3 py-2 text-sm"
          >
            <option value="">All Roles</option>
            <option value="HR">HR</option>
            <option value="HRO">HRO</option>
          </select>
          <select
            value={filters.userId}
            onChange={(e) => setFilters((prev) => ({ ...prev, userId: e.target.value }))}
            className="border rounded-md px-3 py-2 text-sm"
          >
            <option value="">All Employees</option>
            {hrUsers.map((user) => (
              <option key={String(user._id)} value={String(user._id)}>
                {user.name || user.employeeId || user.phoneNumber} {user.employeeId ? `(${user.employeeId})` : ''}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            placeholder="Search path or URL"
            className="border rounded-md px-3 py-2 text-sm"
          />
          <button
            type="submit"
            className="bg-blue-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-blue-700"
          >
            Apply
          </button>
        </form>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left p-3">Date & Time</th>
                <th className="text-left p-3">Employee</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Path</th>
                <th className="text-left p-3">IP</th>
                <th className="text-left p-3">Device</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="p-4 text-gray-500" colSpan={6}>Loading logs...</td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td className="p-4 text-gray-500" colSpan={6}>No logs found</td>
                </tr>
              ) : (
                logs.map((item) => (
                  <tr key={item._id} className="border-t">
                    <td className="p-3">{formatDateTime(item.visitedAt)}</td>
                    <td className="p-3">
                      <div className="font-medium text-gray-900">{item.userId?.name || 'Unknown'}</div>
                      <div className="text-xs text-gray-500">{item.userId?.employeeId || '-'}</div>
                    </td>
                    <td className="p-3">{item.role}</td>
                    <td className="p-3">
                      <div className="font-mono text-xs text-gray-800">{item.path}</div>
                    </td>
                    <td className="p-3">{item.ipAddress || '-'}</td>
                    <td className="p-3">
                      <div className="font-mono text-xs text-gray-700">{item.deviceId || '-'}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between text-sm text-gray-600">
          <div>Total: {pagination.total || 0}</div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={(pagination.page || 1) <= 1}
              onClick={() => fetchLogs((pagination.page || 1) - 1, appliedFilters)}
              className="px-3 py-1.5 border rounded disabled:opacity-50"
            >
              Prev
            </button>
            <span className="px-2 py-1.5">Page {pagination.page || 1} / {pagination.totalPages || 1}</span>
            <button
              type="button"
              disabled={(pagination.page || 1) >= (pagination.totalPages || 1)}
              onClick={() => fetchLogs((pagination.page || 1) + 1, appliedFilters)}
              className="px-3 py-1.5 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ChairmanLogsPage;
